import React, { useCallback, useRef } from 'react';
import { Radio } from '@sravni/react-design-system';
import { mergeRefs } from '@sravni/react-utils';
import styles from './styles.module.scss';
import { ErrorTextWrapper } from '../input/errorTextWrapper';

type RadioItem = {
    value: string;
    label?: React.ReactNode;
    disabled?: boolean;
    readOnly?: boolean;
    className?: string;
};

type Props = {
    list: Array<RadioItem>;
    errorMessage: React.ReactNode | undefined;
    additionalMessage?: React.ReactNode;
} & Omit<React.ComponentProps<'input'>, 'list'>;

export const RadioGroup = React.forwardRef<HTMLInputElement, Props>((props, ref) => {
    const { list, errorMessage, additionalMessage, value: currentValue, onChange, onBlur, ...other } = props;
    const inputRef = useRef<HTMLInputElement>(null);
    const isPropagateEvent = useRef<boolean>(false);

    const onChangeHandler: Props['onChange'] = useCallback(
        (event) => {
            if (onChange) {
                onChange(event);
            }

            const { current } = inputRef;
            if (current) {
                isPropagateEvent.current = true;
                current.blur();
            }
        },
        [onChange],
    );

    const onBlurHandler: React.FocusEventHandler = useCallback((event) => {
        if (!isPropagateEvent.current) {
            event.stopPropagation();
        } else {
            isPropagateEvent.current = false;
        }
    }, []);

    return (
        <ErrorTextWrapper errorText={errorMessage} additionalText={additionalMessage}>
            <div className={styles.root} onBlur={onBlurHandler}>
                <input ref={mergeRefs(ref, inputRef)} type="hidden" {...other} value={currentValue || ''} />
                {list.map(({ label, value, ...args }) => (
                    <Radio
                        key={value}
                        value={value}
                        name={props.name}
                        {...args}
                        checked={currentValue === value}
                        onBlur={onBlur}
                        onChange={onChangeHandler}>
                        {label || value}
                    </Radio>
                ))}
            </div>
        </ErrorTextWrapper>
    );
});

RadioGroup.displayName = 'RadioGroup';
